import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Alert, 
  Spinner, 
  Badge,
  Container,
  Table,
  Form,
  InputGroup,
  Modal,
  Toast,
  ToastContainer,
  Pagination,
  ProgressBar
} from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import axios from "axios";
import * as XLSX from 'xlsx';
import Select from 'react-select';
import {
  PersonPlus,
  Calendar,
  CheckCircle,
  InfoCircle,
  Person,
  Search,
  FileText,
  Wallet2,
  PlusCircle,
  ArrowRepeat,
  XCircle,
  Clock,
  Award,
  Building,
  Briefcase,
  PeopleFill,
  CurrencyDollar,
  CreditCard,
  FileEarmarkExcel,
  FilePdf,
  Phone
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../constants/api_constants";

const EnrolmentBonuses = () => {
  const navigate = useNavigate();
  
  // ✅ Date du jour et 1er jour du mois
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstDayStr = firstDayOfMonth.toISOString().slice(0, 10);

  // États principaux
  const [loading, setLoading] = useState(true);
  const [bonuses, setBonuses] = useState([]);
  const [regeneratingAll, setRegeneratingAll] = useState(false);
  const [stats, setStats] = useState(null);
  const [startDate, setStartDate] = useState(firstDayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [agents, setAgents] = useState([]);
  const [totalAgents, setTotalAgents] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [autoRegenerating, setAutoRegenerating] = useState(false);
  const [hasAutoRegenerated, setHasAutoRegenerated] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isRegeneratingAfterLoad, setIsRegeneratingAfterLoad] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  
  // États pour les filtres temporaires (avant validation)
  const [tempStartDate, setTempStartDate] = useState(firstDayStr);
  const [tempEndDate, setTempEndDate] = useState(todayStr);
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [tempItemsPerPage, setTempItemsPerPage] = useState(10);
  
  // Pagination côté API
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0
  });
  
  // États pour les modales
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedBonus, setSelectedBonus] = useState(null);
  const [paying, setPaying] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [paymentReference, setPaymentReference] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showCalculateModal, setShowCalculateModal] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [calculatingAll, setCalculatingAll] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [accountingData, setAccountingData] = useState(null);
  const [showAccountingModal, setShowAccountingModal] = useState(false);
  
  // États pour le paiement
  const [mobileNumber, setMobileNumber] = useState('');
  const [mobileOperator, setMobileOperator] = useState('orange');
  const [paymentNotes, setPaymentNotes] = useState('');
  
  // Ref pour empêcher les appels en double
  const isCalculatingRef = useRef(false);
  const searchTimeoutRef = useRef(null);
  const isLoadingRef = useRef(false);
  const recalculatingTimeoutRef = useRef(null);
  const isRegeneratingRef = useRef(false);
  const isInitializedRef = useRef(false);
  
  // Toast notifications
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [toastTitle, setToastTitle] = useState('');

  // Récupérer la devise depuis la session
  const defaultCurrency = sessionStorage.getItem("default_currency") || 'USD';

  const getAuthToken = () => sessionStorage.getItem("authToken");
  
  const getHeaders = () => {
    const token = getAuthToken();
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  };

  const showToastNotification = (title, message, variant = 'success') => {
    setToastTitle(title);
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 5000);
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: defaultCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Date inconnue";
    try {
      let date;
      if (dateString instanceof Date) {
        date = dateString;
      } else if (typeof dateString === 'number') {
        date = new Date(dateString);
      } else {
        const cleanDate = dateString.replace(/-/g, '/');
        date = new Date(cleanDate);
      }
      if (isNaN(date.getTime())) {
        return dateString;
      }
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatPeriod = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })} - ${end.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}`;
  };

  // ✅ Validation des dates
  const validateDates = useCallback(() => {
    if (!startDate || !endDate) {
      showToastNotification('⚠️ Attention', 'Veuillez sélectionner une période valide', 'warning');
      return false;
    }
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      showToastNotification('⚠️ Erreur', 'Format de date invalide', 'danger');
      return false;
    }
    
    if (start > end) {
      showToastNotification('⚠️ Attention', 'La date de début ne peut pas être après la date de fin', 'warning');
      return false;
    }
    
    return true;
  }, [startDate, endDate]);

  // Options pour react-select
  const agentOptions = React.useMemo(() => {
    return agents.map(agent => ({
      value: agent.user_id,
      label: `${agent.nom_complet} - ${agent.email || agent.telephone || 'N/A'}`
    }));
  }, [agents]);

  // 🔥 RECALCULER LES PRIMES POUR UNE PAGE
  const recalculateCurrentPageBonuses = useCallback(async (page = currentPage, search = searchTerm, silent = false) => {
    if (isRegeneratingRef.current) {
      console.log("⏳ Régénération déjà en cours, ignore");
      return false;
    }
    
    // Récupérer les agents de la page
    let agentsList = agents;
    if (agentsList.length === 0) {
      if (!silent) {
        showToastNotification('⚠️ Attention', 'Aucun agent à recalculer', 'warning');
      }
      return false;
    }
    
    isRegeneratingRef.current = true;
    setAutoRegenerating(true);
    setIsRecalculating(true);
    
    try {
      console.log(`🔄 Recalcul de la page ${page} avec ${agentsList.length} agents...`);
      
      // 🔥 APPEL UNIQUE À L'API pour recalculer toute la page
      const response = await axios.post(
        `${API_BASE_URL}/agent-finance/recalculate/enrolment-page`,
        {
          date_debut: startDate,
          date_fin: endDate,
          page: page,
          per_page: itemsPerPage,
          search: search
        },
        { headers: getHeaders() }
      );
      
      console.log("✅ Réponse du recalcul:", response.data);
      
      if (response.data.success) {
        const data = response.data.data;
        
        if (!silent) {
          const successCount = data.success || 0;
          const errorCount = data.errors || 0;
          
          showToastNotification(
            '✅ Recalcul terminé',
            `${successCount} agents recalculés${errorCount > 0 ? `, ${errorCount} échecs` : ''}`,
            errorCount > 0 ? 'warning' : 'success'
          );
        }
        
        return true;
      } else {
        if (!silent) {
          showToastNotification('❌ Erreur', response.data.message || 'Erreur lors du recalcul', 'danger');
        }
        return false;
      }
      
    } catch (error) {
      console.error("❌ Erreur recalcul page:", error);
      
      // 🔥 En cas d'erreur, essayer la méthode fallback (agent par agent)
      console.log("🔄 Fallback: recalcul agent par agent...");
      
      try {
        let successCount = 0;
        let errorCount = 0;
        
        for (const agent of agentsList) {
          try {
            await axios.post(
              `${API_BASE_URL}/agent-finance/calculate/enrolment`,
              { agent_id: agent.user_id, date_debut: startDate, date_fin: endDate },
              { headers: getHeaders() }
            );
            successCount++;
          } catch (err) {
            errorCount++;
            console.error(`❌ Erreur pour l'agent ${agent.user_id}:`, err);
          }
        }
        
        if (!silent) {
          showToastNotification(
            '✅ Recalcul terminé (fallback)',
            `${successCount} agents recalculés, ${errorCount} échecs`,
            errorCount > 0 ? 'warning' : 'success'
          );
        }
        
        return successCount > 0;
        
      } catch (fallbackError) {
        if (!silent) {
          showToastNotification('❌ Erreur', 'Erreur lors du recalcul des agents', 'danger');
        }
        return false;
      }
      
    } finally {
      isRegeneratingRef.current = false;
      setAutoRegenerating(false);
      setIsRecalculating(false);
    }
  }, [agents, currentPage, itemsPerPage, searchTerm, startDate, endDate]);


  
  // 🔥 CHARGER LES DONNÉES AVEC PAGINATION API - VERSION SIMPLIFIÉE
const loadAllData = useCallback(async (page = 1, search = searchTerm, forceRecalculate = false) => {
  // ✅ Validation des dates
  if (!validateDates()) {
    setLoading(false);
    setIsLoading(false);
    isLoadingRef.current = false;
    return;
  }
  
  // Empêcher les appels simultanés
  if (isLoadingRef.current) {
    console.log("⏳ Chargement déjà en cours, ignore");
    return;
  }
  
  isLoadingRef.current = true;
  
  try {
    setIsLoading(true);
    setError(null);
    
    const effectiveSearch = search !== undefined ? search : searchTerm;
    
    // 🔥 SI RECALCUL FORCÉ, ON LE FAIT ICI
    if (forceRecalculate) {
      console.log("🔄 Recalcul forcé demandé pour la période", { startDate, endDate, page, search: effectiveSearch });
      
      // Récupérer d'abord les agents
      const tempResponse = await axios.get(`${API_BASE_URL}/agent-finance/enrolment-agents-paginated`, { 
        headers: getHeaders(),
        params: { 
          date_debut: startDate,
          date_fin: endDate,
          page: page,
          per_page: itemsPerPage,
          search: effectiveSearch,
          sort_field: 'contribuables_count',
          sort_direction: 'desc'
        }
      });
      
      const tempData = tempResponse.data.data;
      const tempAgents = tempData.agents || [];
      
      if (tempAgents.length > 0) {
        // Recalculer chaque agent
        for (const agent of tempAgents) {
          try {
            await axios.post(
              `${API_BASE_URL}/agent-finance/calculate/enrolment`,
              { agent_id: agent.user_id, date_debut: startDate, date_fin: endDate },
              { headers: getHeaders() }
            );
          } catch (err) {
            console.error(`❌ Erreur pour l'agent ${agent.user_id}:`, err);
          }
        }
      }
    }
    
    // Charger les données finales
    const response = await axios.get(`${API_BASE_URL}/agent-finance/enrolment-agents-paginated`, { 
      headers: getHeaders(),
      params: { 
        date_debut: startDate,
        date_fin: endDate,
        page: page,
        per_page: itemsPerPage,
        search: effectiveSearch,
        sort_field: 'contribuables_count',
        sort_direction: 'desc'
      }
    });
    
    console.log("🔍 Réponse API paginée:", response.data);
    
    const data = response.data.data;
    const agentsList = data.agents || [];
    const statsData = data.stats || null;
    const paginationData = data.pagination || null;
    
    console.log("🔍 Agents reçus:", agentsList.length);
    console.log("🔍 Pagination:", paginationData);
    console.log("🔍 Recherche appliquée:", effectiveSearch);
    
    setAgents(agentsList);
    setTotalAgents(paginationData?.total || 0);
    
    setPagination({
      currentPage: paginationData?.current_page || 1,
      perPage: paginationData?.per_page || 10,
      total: paginationData?.total || 0,
      lastPage: paginationData?.last_page || 1,
      from: paginationData?.from || 0,
      to: paginationData?.to || 0
    });
    
    setCurrentPage(paginationData?.current_page || 1);
    
    const bonusesList = agentsList
      .filter(agent => agent.bonus)
      .map(agent => {
        const bonus = agent.bonus;
        const amount = parseFloat(bonus.amount) || 0;
        const isPaid = bonus.status === 'paid' || bonus.is_fully_paid === true;
        
        return {
          id: bonus.id,
          user_id: agent.user_id,
          amount: amount,
          status: bonus.status || 'pending',
          contribuables_count: bonus.contribuables_count || 0,
          payment_date: bonus.payment_date || null,
          period_start: bonus.period_start || startDate,
          period_end: bonus.period_end || endDate,
          bonus_type: bonus.bonus_type || 'enrollement',
          agent: {
            name: agent.nom_complet || `Agent ${agent.user_id}`,
            email: agent.email || 'N/A',
            phone: agent.telephone || 'N/A'
          },
          total_amount: parseFloat(bonus.total_amount || amount),
          total_paid: parseFloat(bonus.total_paid || (isPaid ? amount : 0)),
          total_pending: parseFloat(bonus.total_pending || (isPaid ? 0 : amount)),
          complements: bonus.complements || [],
          has_complement: bonus.has_complement || false,
          is_fully_paid: isPaid
        };
      });

    console.log("🔍 Bonus traités:", bonusesList.length);
    
    setBonuses(bonusesList);
    
    if (statsData && statsData.total_amount > 0) {
      setStats(statsData);
    } else {
      const totalAgentsCount = agentsList.length;
      const totalContribuables = bonusesList.reduce((sum, b) => sum + (b.contribuables_count || 0), 0);
      const totalDebit = bonusesList.reduce((sum, b) => sum + (b.total_amount || 0), 0);
      const totalPaid = bonusesList.reduce((sum, b) => sum + (b.total_paid || 0), 0);
      
      setStats({
        total_agents: totalAgentsCount,
        total_contribuables: totalContribuables,
        total_amount: totalDebit,
        paid_amount: totalPaid,
        pending_amount: totalDebit - totalPaid,
        rate_per_contribuable: 0.50
      });
    }
    
    const totalDebit = bonusesList.reduce((sum, b) => sum + (b.total_amount || 0), 0);
    const totalCredit = bonusesList.reduce((sum, b) => sum + (b.total_paid || 0), 0);
    const balance = totalDebit - totalCredit;
    
    setAccountingData({
      date_debut: startDate,
      date_fin: endDate,
      debit: totalDebit,
      credit: totalCredit,
      balance: balance,
      isSettled: balance <= 0,
      pendingCount: bonusesList.filter(b => b.status === 'pending').length,
      paidCount: bonusesList.filter(b => b.status === 'paid' || b.is_fully_paid).length,
      totalBonuses: bonusesList.length
    });
    
    setIsDataLoaded(true);
    setLoading(false);
    
  } catch (err) {
    console.error("❌ Erreur chargement:", err);
    setError(err.response?.data?.message || "Erreur lors du chargement des données");
    setLoading(false);
  } finally {
    setIsLoading(false);
    isLoadingRef.current = false;
  }
}, [startDate, endDate, itemsPerPage, searchTerm, validateDates, getHeaders]);


  // 🔥 Fonction refreshData pour recharger les données
  const refreshData = useCallback(async (forceRecalculate = false) => {
    // Si un chargement est déjà en cours, attendre qu'il se termine
    if (isLoadingRef.current) {
      console.log("⏳ RefreshData: chargement déjà en cours, on attend...");
      let waitCount = 0;
      while (isLoadingRef.current && waitCount < 30) {
        await new Promise(resolve => setTimeout(resolve, 100));
        waitCount++;
      }
      if (isLoadingRef.current) {
        console.log("⚠️ Timeout: le chargement est toujours en cours");
        return;
      }
    }
    await loadAllData(currentPage, searchTerm, forceRecalculate);
  }, [loadAllData, currentPage, searchTerm]);

  // 🔥 Régénérer les agents de la page courante (manuel)
  const handleRecalculateCurrentPage = useCallback(async () => {
    const success = await recalculateCurrentPageBonuses(currentPage, searchTerm, false);
    if (success) {
      await loadAllData(currentPage, searchTerm, false);
    }
  }, [recalculateCurrentPageBonuses, loadAllData, currentPage, searchTerm]);

  // 🔥 Calculer les primes pour tous les agents
  const handleCalculateAll = useCallback(async () => {
    // ✅ Validation des dates
    if (!validateDates()) return;
    
    if (isCalculatingRef.current) {
      console.log("Calcul déjà en cours, ignore");
      return;
    }

    isCalculatingRef.current = true;
    setCalculatingAll(true);
    
    try {
      console.log("=== CALCUL POUR TOUS LES AGENTS ===");
      console.log("Période:", startDate, "->", endDate);
      
      // Récupérer tous les agents (toutes les pages)
      let allAgents = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore) {
        const response = await axios.get(`${API_BASE_URL}/agent-finance/enrolment-agents-paginated`, {
          headers: getHeaders(),
          params: {
            date_debut: startDate,
            date_fin: endDate,
            page: page,
            per_page: 100,
            search: '',
            sort_field: 'contribuables_count',
            sort_direction: 'desc'
          }
        });
        
        const data = response.data.data;
        const agentsList = data.agents || [];
        const paginationData = data.pagination || {};
        
        allAgents = [...allAgents, ...agentsList];
        
        hasMore = page < paginationData.last_page;
        page++;
        
        console.log(`📡 Page ${page - 1}: ${agentsList.length} agents, total: ${allAgents.length}`);
      }
      
      if (allAgents.length === 0) {
        showToastNotification('⚠️ Attention', 'Aucun agent disponible pour le calcul', 'warning');
        setCalculatingAll(false);
        isCalculatingRef.current = false;
        return;
      }
      
      console.log(`📊 Total agents à calculer: ${allAgents.length}`);
      
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      
      for (const agent of allAgents) {
        try {
          const response = await axios.post(
            `${API_BASE_URL}/agent-finance/calculate/enrolment`,
            { agent_id: agent.user_id, date_debut: startDate, date_fin: endDate },
            { headers: getHeaders() }
          );
          
          if (response.data.success) {
            successCount++;
          } else {
            errorCount++;
            errors.push({ agent_id: agent.user_id, error: response.data.message });
          }
        } catch (error) {
          errorCount++;
          errors.push({ agent_id: agent.user_id, error: error.message });
          console.error(`❌ Erreur pour l'agent ${agent.user_id}:`, error);
        }
      }
      
      console.log("=== RÉSULTATS ===");
      console.log(`✅ Succès: ${successCount}, ❌ Échecs: ${errorCount}`);
      if (errors.length > 0) {
        console.log("📋 Erreurs:", errors);
      }
    
      showToastNotification(
        '📊 Calcul terminé', 
        `${successCount} succès, ${errorCount} échecs${errors.length > 0 ? ` (${errors.length} erreurs)` : ''}`,
        errorCount > 0 ? 'warning' : 'success'
      );
      
      // Recharger les données sans recalculer automatiquement
      await loadAllData(currentPage, searchTerm, false);
      
    } catch (error) {
      console.error("❌ Erreur lors du calcul pour tous:", error);
      showToastNotification('❌ Erreur', "Erreur lors du calcul pour tous les agents", 'danger');
    } finally {
      isCalculatingRef.current = false;
      setCalculatingAll(false);
    }
  }, [startDate, endDate, currentPage, searchTerm, loadAllData, validateDates]);

  // ==================== GESTION DES FILTRES ====================

// 🔥 APPLIQUER LES FILTRES AVEC RECALCUL AUTOMATIQUE
const handleApplyFilters = useCallback(async () => {
  // Valider les dates temporaires
  if (!tempStartDate || !tempEndDate) {
    showToastNotification('⚠️ Attention', 'Veuillez sélectionner une période valide', 'warning');
    return;
  }
  
  const start = new Date(tempStartDate);
  const end = new Date(tempEndDate);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    showToastNotification('⚠️ Erreur', 'Format de date invalide', 'danger');
    return;
  }
  
  if (start > end) {
    showToastNotification('⚠️ Attention', 'La date de début ne peut pas être après la date de fin', 'warning');
    return;
  }
  
  // 🔥 SAUVEGARDER LES ANCIENNES VALEURS
  const oldStartDate = startDate;
  const oldEndDate = endDate;
  const oldSearchTerm = searchTerm;
  
  // 🔥 APPLIQUER LES FILTRES
  setStartDate(tempStartDate);
  setEndDate(tempEndDate);
  setSearchTerm(tempSearchTerm);
  setItemsPerPage(tempItemsPerPage);
  setCurrentPage(1);
  setHasAutoRegenerated(false);
  
  showToastNotification('✅ Filtres appliqués', 'Recalcul des primes en cours...', 'info');
  
  try {
    setIsLoading(true);
    
    // 🔥 ÉTAPE 1: Récupérer les agents avec les nouveaux filtres
    const response = await axios.get(`${API_BASE_URL}/agent-finance/enrolment-agents-paginated`, { 
      headers: getHeaders(),
      params: { 
        date_debut: tempStartDate,
        date_fin: tempEndDate,
        page: 1,
        per_page: tempItemsPerPage,
        search: tempSearchTerm,
        sort_field: 'contribuables_count',
        sort_direction: 'desc'
      }
    });
    
    const data = response.data.data;
    const agentsList = data.agents || [];
    const statsData = data.stats || null;
    const paginationData = data.pagination || null;
    
    console.log("🔍 Agents après filtres:", agentsList.length);
    console.log("🔍 Pagination:", paginationData);
    
    if (agentsList.length === 0) {
      setAgents([]);
      setBonuses([]);
      setTotalAgents(0);
      setPagination({
        currentPage: 1,
        perPage: tempItemsPerPage,
        total: 0,
        lastPage: 1,
        from: 0,
        to: 0
      });
      setAccountingData(null);
      setStats(null);
      setIsLoading(false);
      showToastNotification('ℹ️ Information', 'Aucun agent trouvé avec ces filtres', 'info');
      return;
    }
    
    // 🔥 ÉTAPE 2: Recalculer les primes pour TOUS les agents de la page
    const agentIds = agentsList.map(a => a.user_id).filter(id => id !== null && id !== undefined);
    
    if (agentIds.length > 0) {
      showToastNotification('🔄 Recalcul en cours', `Recalcul des primes pour ${agentIds.length} agents...`, 'info');
      
      let successCount = 0;
      let errorCount = 0;
      
      // Recalculer chaque agent individuellement
      for (const agentId of agentIds) {
        try {
          await axios.post(
            `${API_BASE_URL}/agent-finance/calculate/enrolment`,
            { 
              agent_id: agentId, 
              date_debut: tempStartDate, 
              date_fin: tempEndDate 
            },
            { headers: getHeaders() }
          );
          successCount++;
        } catch (err) {
          errorCount++;
          console.error(`❌ Erreur pour l'agent ${agentId}:`, err);
        }
      }
      
      console.log(`✅ Recalcul terminé: ${successCount} succès, ${errorCount} échecs`);
      
      // 🔥 ÉTAPE 3: Recharger les données après recalcul
      const reloadResponse = await axios.get(`${API_BASE_URL}/agent-finance/enrolment-agents-paginated`, { 
        headers: getHeaders(),
        params: { 
          date_debut: tempStartDate,
          date_fin: tempEndDate,
          page: 1,
          per_page: tempItemsPerPage,
          search: tempSearchTerm,
          sort_field: 'contribuables_count',
          sort_direction: 'desc'
        }
      });
      
      const reloadData = reloadResponse.data.data;
      const reloadAgents = reloadData.agents || [];
      const reloadStats = reloadData.stats || null;
      const reloadPagination = reloadData.pagination || null;
      
      // 🔥 ÉTAPE 4: Mettre à jour l'état
      setAgents(reloadAgents);
      setTotalAgents(reloadPagination?.total || 0);
      
      setPagination({
        currentPage: reloadPagination?.current_page || 1,
        perPage: reloadPagination?.per_page || 10,
        total: reloadPagination?.total || 0,
        lastPage: reloadPagination?.last_page || 1,
        from: reloadPagination?.from || 0,
        to: reloadPagination?.to || 0
      });
      
      setCurrentPage(reloadPagination?.current_page || 1);
      
      // Construire la liste des bonuses
      const bonusesList = reloadAgents
        .filter(agent => agent.bonus)
        .map(agent => {
          const bonus = agent.bonus;
          const amount = parseFloat(bonus.amount) || 0;
          const isPaid = bonus.status === 'paid' || bonus.is_fully_paid === true;
          
          return {
            id: bonus.id,
            user_id: agent.user_id,
            amount: amount,
            status: bonus.status || 'pending',
            contribuables_count: bonus.contribuables_count || 0,
            payment_date: bonus.payment_date || null,
            period_start: bonus.period_start || tempStartDate,
            period_end: bonus.period_end || tempEndDate,
            bonus_type: bonus.bonus_type || 'enrollement',
            agent: {
              name: agent.nom_complet || `Agent ${agent.user_id}`,
              email: agent.email || 'N/A',
              phone: agent.telephone || 'N/A'
            },
            total_amount: parseFloat(bonus.total_amount || amount),
            total_paid: parseFloat(bonus.total_paid || (isPaid ? amount : 0)),
            total_pending: parseFloat(bonus.total_pending || (isPaid ? 0 : amount)),
            complements: bonus.complements || [],
            has_complement: bonus.has_complement || false,
            is_fully_paid: isPaid
          };
        });
      
      setBonuses(bonusesList);
      
      // Mettre à jour les statistiques
      if (reloadStats && reloadStats.total_amount > 0) {
        setStats(reloadStats);
      } else {
        const totalContribuables = bonusesList.reduce((sum, b) => sum + (b.contribuables_count || 0), 0);
        const totalDebit = bonusesList.reduce((sum, b) => sum + (b.total_amount || 0), 0);
        const totalPaid = bonusesList.reduce((sum, b) => sum + (b.total_paid || 0), 0);
        
        setStats({
          total_agents: reloadAgents.length,
          total_contribuables: totalContribuables,
          total_amount: totalDebit,
          paid_amount: totalPaid,
          pending_amount: totalDebit - totalPaid,
          rate_per_contribuable: 0.50
        });
      }
      
      // Calculer les données comptables
      const totalDebit = bonusesList.reduce((sum, b) => sum + (b.total_amount || 0), 0);
      const totalCredit = bonusesList.reduce((sum, b) => sum + (b.total_paid || 0), 0);
      const balance = totalDebit - totalCredit;
      
      setAccountingData({
        date_debut: tempStartDate,
        date_fin: tempEndDate,
        debit: totalDebit,
        credit: totalCredit,
        balance: balance,
        isSettled: balance <= 0,
        pendingCount: bonusesList.filter(b => b.status === 'pending').length,
        paidCount: bonusesList.filter(b => b.status === 'paid' || b.is_fully_paid).length,
        totalBonuses: bonusesList.length
      });
      
      setIsDataLoaded(true);
      
      showToastNotification(
        '✅ Succès', 
        `Filtres appliqués et ${successCount} primes recalculées${errorCount > 0 ? `, ${errorCount} échecs` : ''}`,
        errorCount > 0 ? 'warning' : 'success'
      );
      
    } else {
      showToastNotification('⚠️ Attention', 'Aucun agent à recalculer', 'warning');
    }
    
  } catch (error) {
    console.error("❌ Erreur lors du recalcul:", error);
    showToastNotification('❌ Erreur', 'Erreur lors du recalcul: ' + (error.response?.data?.message || error.message), 'danger');
  } finally {
    setIsLoading(false);
    setLoading(false);
  }
}, [tempStartDate, tempEndDate, tempSearchTerm, tempItemsPerPage, startDate, endDate, searchTerm, getHeaders]);




  // 🔥 RÉINITIALISER LES FILTRES
  const handleResetFilters = useCallback(() => {
    setTempStartDate(firstDayStr);
    setTempEndDate(todayStr);
    setTempSearchTerm("");
    setTempItemsPerPage(10);
    
    // Appliquer immédiatement les filtres par défaut
    setStartDate(firstDayStr);
    setEndDate(todayStr);
    setSearchTerm("");
    setItemsPerPage(10);
    setCurrentPage(1);
    setHasAutoRegenerated(false);
    
    showToastNotification('🔄 Réinitialisation', 'Réinitialisation des filtres en cours...', 'info');
    
    loadAllData(1, "", true);
    
    showToastNotification('🔄 Filtres réinitialisés', 'Les filtres ont été réinitialisés', 'info');
  }, [firstDayStr, todayStr, loadAllData]);

  // ==================== PAGINATION ====================
  const handlePageChange = useCallback((page) => {
    if (page >= 1 && page <= pagination.lastPage) {
      setCurrentPage(page);
      console.log(`📄 Changement de page vers ${page}`);
      loadAllData(page, searchTerm, true);
    }
  }, [pagination.lastPage, searchTerm, loadAllData]);

  const getPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    const current = pagination.currentPage;
    const total = pagination.lastPage;
    
    if (total <= 1) return items;
    
    let startPage = Math.max(1, current - Math.floor(maxVisible / 2));
    let endPage = Math.min(total, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.First key="first" onClick={() => handlePageChange(1)} />
      );
      items.push(
        <Pagination.Prev key="prev" onClick={() => handlePageChange(current - 1)} />
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item key={i} active={i === current} onClick={() => handlePageChange(i)}>
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < total) {
      items.push(
        <Pagination.Next key="next" onClick={() => handlePageChange(current + 1)} />
      );
      items.push(
        <Pagination.Last key="last" onClick={() => handlePageChange(total)} />
      );
    }

    return items;
  };

  // ==================== EXPORT EXCEL ====================
  const handleExportExcel = async () => {
    if (!validateDates()) return;
    
    try {
      setExporting(true);
      
      let allAgents = [];
      let page = 1;
      let hasMore = true;
      
      showToastNotification('⏳ Export en cours', 'Récupération des données...', 'info');
      
      while (hasMore) {
        const response = await axios.get(`${API_BASE_URL}/agent-finance/enrolment-agents-paginated`, {
          headers: getHeaders(),
          params: {
            date_debut: startDate,
            date_fin: endDate,
            page: page,
            per_page: 100,
            search: '',
            sort_field: 'contribuables_count',
            sort_direction: 'desc'
          }
        });
        
        const data = response.data.data;
        const agentsList = data.agents || [];
        const paginationData = data.pagination || {};
        
        allAgents = [...allAgents, ...agentsList];
        hasMore = page < paginationData.last_page;
        page++;
      }
      
      if (allAgents.length === 0) {
        showToastNotification('⚠️ Attention', 'Aucun agent à exporter', 'warning');
        setExporting(false);
        return;
      }
      
      const allData = allAgents.map(agent => {
        const bonus = agent.bonus;
        const debit = bonus ? parseFloat(bonus.total_amount || 0) : 0;
        const credit = bonus ? parseFloat(bonus.total_paid || 0) : 0;
        const balance = debit - credit;
        
        return {
          'Agent': agent.nom_complet || 'N/A',
          'Email': agent.email || 'N/A',
          'Téléphone': agent.telephone || 'N/A',
          'Contribuables enrôlés': bonus?.contribuables_count || 0,
          'Débit (Dû)': formatCurrency(debit),
          'Crédit (Payé)': formatCurrency(credit),
          'Solde': formatCurrency(Math.abs(balance)),
          'Statut': bonus ? (bonus.is_fully_paid ? 'Payée' : 'En attente') : 'Non calculée',
          'Date paiement': bonus?.payment_date ? formatDate(bonus.payment_date) : '-',
          'Période': `${formatDate(startDate)} - ${formatDate(endDate)}`
        };
      });

      // Ajouter la synthèse comptable
      const totalDebit = allAgents.reduce((sum, agent) => {
        const bonus = agent.bonus;
        return sum + (bonus ? parseFloat(bonus.total_amount || 0) : 0);
      }, 0);
      
      const totalCredit = allAgents.reduce((sum, agent) => {
        const bonus = agent.bonus;
        return sum + (bonus ? parseFloat(bonus.total_paid || 0) : 0);
      }, 0);
      
      const totalBalance = totalDebit - totalCredit;
      const paidCount = allAgents.filter(a => a.bonus?.is_fully_paid).length;
      const totalCount = allAgents.length;

      allData.push({
        'Agent': '--- SYNTHÈSE COMPTABLE ---',
        'Email': '',
        'Téléphone': '',
        'Contribuables enrôlés': '',
        'Débit (Dû)': '',
        'Crédit (Payé)': '',
        'Solde': '',
        'Statut': '',
        'Date paiement': '',
        'Période': ''
      });
      allData.push({
        'Agent': 'TOTAL DÉBIT',
        'Email': '',
        'Téléphone': '',
        'Contribuables enrôlés': '',
        'Débit (Dû)': formatCurrency(totalDebit),
        'Crédit (Payé)': '',
        'Solde': '',
        'Statut': '',
        'Date paiement': '',
        'Période': ''
      });
      allData.push({
        'Agent': 'TOTAL CRÉDIT',
        'Email': '',
        'Téléphone': '',
        'Contribuables enrôlés': '',
        'Débit (Dû)': '',
        'Crédit (Payé)': formatCurrency(totalCredit),
        'Solde': '',
        'Statut': '',
        'Date paiement': '',
        'Période': ''
      });
      allData.push({
        'Agent': `SOLDE (${totalBalance <= 0 ? 'SOLDÉ' : 'NON SOLDÉ'})`,
        'Email': '',
        'Téléphone': '',
        'Contribuables enrôlés': '',
        'Débit (Dû)': '',
        'Crédit (Payé)': '',
        'Solde': formatCurrency(Math.abs(totalBalance)),
        'Statut': totalBalance <= 0 ? 'Soldé' : 'Non soldé',
        'Date paiement': '',
        'Période': ''
      });
      allData.push({
        'Agent': `STATUT: ${paidCount}/${totalCount} primes payées`,
        'Email': '',
        'Téléphone': '',
        'Contribuables enrôlés': '',
        'Débit (Dû)': '',
        'Crédit (Payé)': '',
        'Solde': '',
        'Statut': '',
        'Date paiement': '',
        'Période': ''
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(allData);
      
      ws['!cols'] = [
        { wch: 30 }, { wch: 25 }, { wch: 15 }, { wch: 20 },
        { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
        { wch: 25 }, { wch: 25 }
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Primes enrôlement');
      
      const fileName = `primes_enrolement_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      showToastNotification('✅ Succès', `Export Excel terminé: ${allAgents.length} agents exportés`, 'success');
    } catch (error) {
      console.error("Erreur export Excel:", error);
      showToastNotification('❌ Erreur', 'Erreur lors de l\'export Excel: ' + error.message, 'danger');
    } finally {
      setExporting(false);
    }
  };

  // ==================== EXPORT PDF ====================
  const handleExportPDF = async () => {
    if (!validateDates()) return;
    
    if (exporting) return;
    setExporting(true);
    
    try {
      let allAgents = [];
      let page = 1;
      let hasMore = true;
      
      showToastNotification('⏳ Export en cours', 'Récupération des données...', 'info');
      
      while (hasMore) {
        const response = await axios.get(`${API_BASE_URL}/agent-finance/enrolment-agents-paginated`, {
          headers: getHeaders(),
          params: {
            date_debut: startDate,
            date_fin: endDate,
            page: page,
            per_page: 100,
            search: '',
            sort_field: 'contribuables_count',
            sort_direction: 'desc'
          }
        });
        
        const data = response.data.data;
        const agentsList = data.agents || [];
        const paginationData = data.pagination || {};
        
        allAgents = [...allAgents, ...agentsList];
        hasMore = page < paginationData.last_page;
        page++;
      }
      
      if (allAgents.length === 0) {
        showToastNotification('⚠️ Attention', 'Aucun agent à exporter', 'warning');
        setExporting(false);
        return;
      }
      
      const totalDebit = allAgents.reduce((sum, agent) => {
        const bonus = agent.bonus;
        return sum + (bonus ? parseFloat(bonus.total_amount || 0) : 0);
      }, 0);
      
      const totalCredit = allAgents.reduce((sum, agent) => {
        const bonus = agent.bonus;
        return sum + (bonus ? parseFloat(bonus.total_paid || 0) : 0);
      }, 0);
      
      const totalBalance = totalDebit - totalCredit;
      const paidCount = allAgents.filter(a => a.bonus?.is_fully_paid).length;
      
      const fileName = `PRIMES_ENROLEMENT_${new Date().toISOString().split('T')[0]}`;
      const originalTitle = document.title;
      document.title = fileName;

      const formatCurrencyPDF = (amount) => {
        const num = parseFloat(amount) || 0;
        return num.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + defaultCurrency;
      };

      const formatDatePDF = (dateString) => {
        if (!dateString) return "-";
        try {
          const date = new Date(dateString);
          return date.toLocaleDateString('fr-FR', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
          });
        } catch (e) { 
          return dateString; 
        }
      };

      const htmlContent = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>${fileName}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; padding: 4mm 5mm; background: white; font-size: 9pt; line-height: 1.25; color: #1a1a1a; }
    .report { max-width: 100%; margin: 0 auto; }
    .header { text-align: center; padding-bottom: 8px; border-bottom: 3px solid #1a3a6a; margin-bottom: 12px; }
    .header h1 { font-size: 16pt; font-weight: bold; text-transform: uppercase; color: #1a3a6a; }
    .header .subtitle { font-size: 10pt; color: #444; }
    .header .meta { font-size: 8pt; color: #555; margin-top: 4px; }
    .section { margin-bottom: 10px; }
    .section-title { font-size: 10pt; font-weight: bold; text-transform: uppercase; border-bottom: 2px solid #999; padding-bottom: 3px; margin-bottom: 6px; color: #1a3a6a; }
    .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 10px; }
    .stat-item { background: #f8f9fa; padding: 8px 12px; border-radius: 4px; text-align: center; }
    .stat-item .label { font-size: 7pt; color: #666; text-transform: uppercase; }
    .stat-item .value { font-size: 11pt; font-weight: bold; }
    .stat-item .value.primary { color: #1a3a6a; }
    .stat-item .value.success { color: #28a745; }
    .stat-item .value.warning { color: #ffc107; }
    .stat-item .value.danger { color: #dc3545; }
    table { width: 100%; border-collapse: collapse; font-size: 8pt; margin-top: 4px; }
    table thead th { background: #1a3a6a; color: white; padding: 4px 6px; text-align: left; font-weight: bold; border: 1px solid #1a3a6a; text-transform: uppercase; }
    table tbody td { padding: 3px 6px; border: 1px solid #ddd; }
    table tbody tr:nth-child(even) { background: #f8f9fa; }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .fw-bold { font-weight: bold; }
    .badge { display: inline-block; padding: 1px 8px; border-radius: 3px; font-size: 7.5pt; font-weight: bold; }
    .badge.paid { background: #d4edda; color: #155724; }
    .badge.pending { background: #fff3cd; color: #856404; }
    .badge.settled { background: #d4edda; color: #155724; }
    .badge.uns settled { background: #fff3cd; color: #856404; }
    .accounting-summary { background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 4px; padding: 8px 12px; margin-bottom: 10px; }
    .accounting-summary .row { display: flex; justify-content: space-around; }
    .accounting-summary .col { text-align: center; }
    .accounting-summary .label { font-size: 7pt; color: #666; text-transform: uppercase; }
    .accounting-summary .value { font-size: 11pt; font-weight: bold; }
    .accounting-summary .value.debit { color: #dc3545; }
    .accounting-summary .value.credit { color: #28a745; }
    .accounting-summary .value.balance { color: #ffc107; }
    .footer { margin-top: 15px; padding-top: 6px; border-top: 1px solid #ccc; font-size: 7.5pt; color: #777; display: flex; justify-content: space-between; }
    @media print { body { padding: 3mm 4mm; } table thead th { background: #1a3a6a !important; color: white !important; } }
  </style>
</head>
<body>
  <div class="report">
    <div class="header">
      <h1>Primes d'enrôlement</h1>
      <div class="subtitle">${formatCurrencyPDF(0.50)} par contribuable enrôlé</div>
      <div class="meta">
        Période: ${formatPeriod()} | 
        ${new Date().toLocaleDateString('fr-FR')} | 
        ${new Date().toLocaleTimeString('fr-FR')}
      </div>
    </div>
    
    <div class="section">
      <div class="section-title">1. Synthèse des indicateurs</div>
      <div class="stats-grid">
        <div class="stat-item"><div class="label">Total agents</div><div class="value primary">${allAgents.length}</div></div>
        <div class="stat-item"><div class="label">Contribuables enrôlés</div><div class="value primary">${stats?.total_contribuables || 0}</div></div>
        <div class="stat-item"><div class="label">Primes payées</div><div class="value success">${paidCount}/${allAgents.length}</div></div>
        <div class="stat-item"><div class="label">Taux de paiement</div><div class="value ${allAgents.length > 0 && paidCount/allAgents.length >= 0.8 ? 'success' : 'warning'}">${allAgents.length > 0 ? Math.round((paidCount/allAgents.length)*100) : 0}%</div></div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">2. Synthèse comptable</div>
      <div class="accounting-summary">
        <div class="row">
          <div class="col"><div class="label">DÉBIT (Primes dues)</div><div class="value debit">${formatCurrencyPDF(totalDebit)}</div></div>
          <div class="col"><div class="label">CRÉDIT (Payé)</div><div class="value credit">${formatCurrencyPDF(totalCredit)}</div></div>
          <div class="col"><div class="label">SOLDE</div><div class="value balance">${formatCurrencyPDF(Math.abs(totalBalance))}</div></div>
          <div class="col"><div class="label">STATUT</div><div class="value"><span class="badge ${totalBalance <= 0 ? 'settled' : 'uns settled'}">${totalBalance <= 0 ? 'SOLDÉ' : 'NON SOLDÉ'}</span></div></div>
        </div>
      </div>
    </div>
    
    <div class="section">
      <div class="section-title">3. Liste des agents (${allAgents.length})</div>
      <table>
        <thead>
          <tr><th style="width:30px;">#</th><th>Agent</th><th>Email</th><th>Contribuables</th><th>Débit (Dû)</th><th>Crédit (Payé)</th><th>Solde</th><th>Statut</th></tr>
        </thead>
        <tbody>
          ${allAgents.map((agent, index) => {
            const bonus = agent.bonus;
            const debit = bonus ? parseFloat(bonus.total_amount || 0) : 0;
            const credit = bonus ? parseFloat(bonus.total_paid || 0) : 0;
            const balance = debit - credit;
            const isSettled = balance <= 0;
            const statusLabel = bonus ? (bonus.is_fully_paid ? 'Payée' : 'En attente') : 'Non calculée';
            const statusClass = bonus ? (bonus.is_fully_paid ? 'paid' : 'pending') : 'not-calculated';
            return `
            <tr>
              <td class="text-center">${index + 1}</td>
              <td><strong>${agent.nom_complet || 'N/A'}</strong></td>
              <td>${agent.email || 'N/A'}</td>
              <td class="text-center">${bonus?.contribuables_count || 0}</td>
              <td class="text-right fw-bold" style="color:#dc3545;">${bonus ? formatCurrencyPDF(debit) : '-'}</td>
              <td class="text-right fw-bold" style="color:#28a745;">${bonus ? formatCurrencyPDF(credit) : '-'}</td>
              <td class="text-right fw-bold" style="color:${isSettled ? '#28a745' : '#ffc107'};">${bonus ? formatCurrencyPDF(Math.abs(balance)) : '-'}</td>
              <td class="text-center"><span class="badge ${statusClass}">${statusLabel}</span></td>
            </tr>
            `;
          }).join('')}
        </tbody>
      </table>
      <p style="margin-top:4px; font-size:7pt; color:#888; text-align:right;">Total: ${allAgents.length} agent(s)</p>
    </div>
    
    <div class="footer">
      <span>Généré par SyncTaxe</span>
      <span>${new Date().toLocaleDateString('fr-FR')} - ${new Date().toLocaleTimeString('fr-FR')}</span>
    </div>
  </div>
</body>
</html>`;

      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(htmlContent);
      iframeDoc.close();
      
      iframe.contentWindow.onload = () => {
        iframe.contentWindow.document.title = fileName;
        iframe.contentWindow.print();
        
        setTimeout(() => {
          document.title = originalTitle;
          if (document.body.contains(iframe)) document.body.removeChild(iframe);
          setExporting(false);
          showToastNotification('✅ Succès', `PDF exporté: ${allAgents.length} agents`, 'success');
        }, 1500);
      };
      
    } catch (err) {
      console.error('Erreur export PDF:', err);
      showToastNotification('❌ Erreur', 'Erreur lors de l\'export PDF: ' + err.message, 'danger');
      setExporting(false);
    }
  };

  // ==================== GESTION DES PRIMES ====================
  
  // Calculer les primes pour un agent spécifique
  const handleCalculateForAgent = async () => {
    if (!validateDates()) return;
    
    if (!selectedAgent) {
      showToastNotification('⚠️ Attention', 'Veuillez sélectionner un agent', 'warning');
      return;
    }

    try {
      setCalculating(true);
      
      const agentId = parseInt(selectedAgent);
      const selectedAgentObj = agents.find(a => a.user_id === agentId);
      const agentName = selectedAgentObj?.nom_complet || selectedAgent || 'Agent';
      
      let bonus = selectedAgentObj?.bonus;
      
      if (!bonus) {
        const response = await axios.post(
          `${API_BASE_URL}/agent-finance/calculate/enrolment`,
          { agent_id: agentId, date_debut: startDate, date_fin: endDate },
          { headers: getHeaders() }
        );
        
        if (response.data && response.data.success !== false) {
          const data = response.data.data || response.data;
          bonus = data.bonus || data;
          await refreshData(false);
        } else {
          showToastNotification('❌ Erreur', response.data?.message || "Erreur lors du calcul", 'danger');
          setCalculating(false);
          return;
        }
      }
      
      if (!bonus) {
        showToastNotification('❌ Erreur', 'Aucun bonus pour cet agent', 'danger');
        setCalculating(false);
        return;
      }
      
      const totalAmount = parseFloat(bonus.total_amount || bonus.amount || 0);
      const totalPaid = parseFloat(bonus.total_paid || 0);
      const totalPending = parseFloat(bonus.total_pending || (totalAmount - totalPaid));
      const enrolmentCount = parseInt(bonus.contribuables_count || 0);
      const bonusId = bonus.id;
      const isFullyPaid = bonus.is_fully_paid || false;
      const complements = bonus.complements || [];
      const pendingComplement = complements.find(c => c.status === 'pending') || null;
      
      const finalStatus = totalPending > 0.01 ? 'pending' : (isFullyPaid ? 'paid' : 'pending');
      
      setResultData({
        agent_name: agentName,
        agent_id: agentId,
        date_debut: startDate,
        date_fin: endDate,
        enrolment_count: enrolmentCount,
        total_amount: totalAmount,
        total_paid: totalPaid,
        total_pending: totalPending,
        amount_per_contribuable: 0.50,
        bonus_id: bonusId,
        status: finalStatus,
        is_fully_paid: totalPending <= 0.01 && totalAmount > 0,
        has_complement: complements.length > 0,
        pending_complement: pendingComplement,
        complements: complements,
        contribuables: []
      });
      setShowResultModal(true);
      setShowCalculateModal(false);
      setSelectedAgent(null);
    } catch (error) {
      const msg = error.response?.data?.message || error.message || "Erreur lors du calcul";
      showToastNotification('❌ Erreur', msg, 'danger');
    } finally {
      setCalculating(false);
    }
  };
  
  // Calculer la prime pour un agent individuel (depuis le tableau)
// Calculer la prime pour un agent individuel (depuis le tableau)
const handleCalculateSingleAgent = async (agentId, agentName) => {
  if (!validateDates()) return;
  
  try {
    setCalculating(true);
    
    const agentNameDisplay = agentName || 'Agent';
    const agentFromList = agents.find(a => parseInt(a.user_id) === parseInt(agentId));
    
    if (!agentFromList) {
      showToastNotification('❌ Erreur', 'Agent non trouvé', 'danger');
      setCalculating(false);
      return;
    }
    
    let bonus = agentFromList.bonus;
    
    // 🔥 SI LE BONUS N'EXISTE PAS, ON LE CALCULE
    if (!bonus) {
      console.log(`🔄 Bonus non trouvé pour l'agent ${agentId}, calcul en cours...`);
      
      try {
        const response = await axios.post(
          `${API_BASE_URL}/agent-finance/calculate/enrolment`,
          { 
            agent_id: agentId, 
            date_debut: startDate, 
            date_fin: endDate 
          },
          { headers: getHeaders() }
        );
        
        if (response.data && response.data.success !== false) {
          const data = response.data.data || response.data;
          bonus = data.bonus || data;
          
          // 🔥 Rafraîchir les données après le calcul
          await refreshData(false);
          
          // 🔥 Récupérer l'agent mis à jour
          const updatedAgent = agents.find(a => parseInt(a.user_id) === parseInt(agentId));
          if (updatedAgent && updatedAgent.bonus) {
            bonus = updatedAgent.bonus;
          }
        } else {
          showToastNotification('❌ Erreur', response.data?.message || "Erreur lors du calcul", 'danger');
          setCalculating(false);
          return;
        }
      } catch (calcError) {
        console.error("❌ Erreur lors du calcul:", calcError);
        showToastNotification('❌ Erreur', calcError.response?.data?.message || "Erreur lors du calcul", 'danger');
        setCalculating(false);
        return;
      }
    }
    
    // 🔥 VÉRIFIER SI LE BONUS EXISTE MAINTENANT
    if (!bonus) {
      showToastNotification('❌ Erreur', 'Aucun bonus trouvé après calcul', 'danger');
      setCalculating(false);
      return;
    }
    
    const totalAmount = parseFloat(bonus.total_amount || bonus.amount || 0);
    const totalPaid = parseFloat(bonus.total_paid || 0);
    const totalPending = parseFloat(bonus.total_pending || (totalAmount - totalPaid));
    const enrolmentCount = parseInt(bonus.contribuables_count || 0);
    const bonusId = bonus.id;
    const isFullyPaid = bonus.is_fully_paid || false;
    const hasComplement = bonus.has_complement || false;
    const complements = bonus.complements || [];
    const pendingComplement = complements.find(c => c.status === 'pending') || null;
    
    const finalStatus = totalPending > 0.01 ? 'pending' : (isFullyPaid ? 'paid' : 'pending');
    
    setResultData({
      agent_name: agentNameDisplay,
      agent_id: agentId,
      date_debut: startDate,
      date_fin: endDate,
      enrolment_count: enrolmentCount,
      total_amount: totalAmount,
      total_paid: totalPaid,
      total_pending: totalPending,
      amount_per_contribuable: 0.50,
      bonus_id: bonusId,
      status: finalStatus,
      is_fully_paid: totalPending <= 0.01 && totalAmount > 0,
      has_complement: hasComplement,
      pending_complement: pendingComplement,
      complements: complements,
      contribuables: [],
      bonus_data: bonus
    });
    
    setShowResultModal(true);
    await refreshData(false);
  } catch (error) {
    console.error("❌ Erreur handleCalculateSingleAgent:", error);
    showToastNotification('❌ Erreur', error.response?.data?.message || "Erreur lors du calcul", 'danger');
  } finally {
    setCalculating(false);
  }
};

  // Payer une prime
  const handlePayBonus = async () => {
    if (!validateDates()) return;
    
    try {
      setPaying(true);
      
      if (!selectedBonus) {
        showToastNotification('⚠️ Erreur', 'Aucune prime sélectionnée', 'danger');
        setPaying(false);
        return;
      }

      let bonusId = selectedBonus.id;
      
      if (selectedBonus.bonus_type === 'enrollement' && selectedBonus.status === 'paid') {
        const complement = bonuses.find(b => 
          b.user_id === selectedBonus.user_id && 
          b.bonus_type === 'enrollement_complement' && 
          b.status === 'pending'
        );
        
        if (complement) {
          bonusId = complement.id;
          showToastNotification('ℹ️ Information', 'Paiement du complément en attente', 'info');
        } else {
          showToastNotification('⚠️ Erreur', 'Aucun complément en attente trouvé', 'danger');
          setPaying(false);
          return;
        }
      }

      const amountToPay = selectedBonus.amount || selectedBonus.remaining_balance || 0;
      
      if (amountToPay <= 0) {
        showToastNotification('⚠️ Attention', 'Le montant à payer est nul', 'warning');
        setPaying(false);
        return;
      }

      const paymentData = {
        bonus_ids: [bonusId],
        payment_method: paymentMethod,
        reference: paymentReference || `PAY-${Date.now()}`,
        pay_balance: true
      };

      if (paymentMethod === 'mobile_money') {
        paymentData.mobile_number = mobileNumber;
      }

      if (paymentNotes) {
        paymentData.notes = paymentNotes;
      }

      const response = await axios.post(
        `${API_BASE_URL}/agent-finance/pay`,
        paymentData,
        { headers: getHeaders() }
      );

      if (response.data.success) {
        showToastNotification('✅ Paiement effectué', `Paiement de ${formatCurrency(amountToPay)} effectué`, 'success');
        setShowPayModal(false);
        setPaymentReference('');
        setMobileNumber('');
        setMobileOperator('orange');
        setPaymentNotes('');
        await refreshData(true);
      } else {
        showToastNotification('❌ Erreur', response.data.message || "Erreur lors du paiement", 'danger');
      }
    } catch (error) {
      console.error("❌ Erreur paiement:", error);
      showToastNotification('❌ Erreur', error.response?.data?.message || error.message || "Erreur lors du paiement", 'danger');
    } finally {
      setPaying(false);
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle, progress }) => (
    <Col md={3} sm={6} className="mb-3">
      <Card className="h-100 border-0 shadow-sm stats-card">
        <div className={`card-gradient bg-${color}`}></div>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h6 className="text-muted mb-2 small text-uppercase fw-bold">{title}</h6>
              <h3 className="mb-0 fw-bold">{value}</h3>
              {subtitle && <small className="text-muted">{subtitle}</small>}
              {progress !== undefined && (
                <ProgressBar 
                  now={progress} 
                  variant={progress >= 80 ? 'success' : progress >= 50 ? 'warning' : 'danger'}
                  className="mt-2"
                  style={{ height: '6px' }}
                />
              )}
            </div>
            <div className={`bg-${color} bg-opacity-10 rounded-circle p-3`}>
              {icon}
            </div>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );

  // Vérifier si les dates sont valides
  const isDatesValid = startDate && endDate && new Date(startDate) <= new Date(endDate);

  // 🔥 CHARGEMENT INITIAL - UN SEUL APPEL
  useEffect(() => {
    let isMounted = true;
    
    const initializePage = async () => {
      if (isInitializedRef.current) return;
      isInitializedRef.current = true;
      
      if (!isMounted) return;
      await loadAllData(1, '', false);
    };
    
    initializePage();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Indicateur de chargement
  if (loading || isLoading || calculatingAll || isRegeneratingAfterLoad) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f5f7fa', minHeight: '100vh' }}>
          <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <Card className="border-0 shadow-sm rounded-4 text-center" style={{ maxWidth: '500px', width: '100%' }}>
              <Card.Body className="p-5">
                <div className="mb-4">
                  <div className="pulse-animation mx-auto d-flex align-items-center justify-content-center" 
                       style={{ width: '80px', height: '80px', background: 'linear-gradient(135deg, #0d6efd, #0a58ca)', borderRadius: '50%' }}>
                    <Spinner animation="border" variant="light" style={{ width: '2rem', height: '2rem' }} />
                  </div>
                </div>
                <h4 className="fw-bold mb-2">Chargement en cours</h4>
                <p className="text-muted mb-4">Récupération des informations de primes des agents...</p>
                <div className="d-flex justify-content-center gap-2 mb-4">
                  <div className="spinner-grow spinner-grow-sm text-primary" style={{ animationDelay: '0s' }} />
                  <div className="spinner-grow spinner-grow-sm text-primary" style={{ animationDelay: '0.15s' }} />
                  <div className="spinner-grow spinner-grow-sm text-primary" style={{ animationDelay: '0.3s' }} />
                  <div className="spinner-grow spinner-grow-sm text-primary" style={{ animationDelay: '0.45s' }} />
                  <div className="spinner-grow spinner-grow-sm text-primary" style={{ animationDelay: '0.6s' }} />
                </div>
                <div className="mx-auto" style={{ width: '200px' }}>
                  <div className="progress" style={{ height: '4px', borderRadius: '2px' }}>
                    <div className="progress-bar progress-bar-striped progress-bar-animated bg-primary" style={{ width: '100%' }} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Container>
          <style>{`
            .pulse-animation { animation: pulse 1.5s ease-in-out infinite; }
            @keyframes pulse {
              0% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(13,110,253,0.4); }
              50% { transform: scale(1.05); opacity: 0.9; box-shadow: 0 0 0 15px rgba(13,110,253,0); }
              100% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(13,110,253,0); }
            }
          `}</style>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <Container fluid>
          <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
            <Toast show={showToast} onClose={() => setShowToast(false)} bg={toastVariant} delay={5000} autohide>
              <Toast.Header><strong className="me-auto">{toastTitle}</strong></Toast.Header>
              <Toast.Body className={toastVariant === 'success' ? 'text-white' : ''}>{toastMessage}</Toast.Body>
            </Toast>
          </ToastContainer>

          {/* Header */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                <div className="mb-3 mb-md-0">
                  <ol className="breadcrumb fs-sm mb-1">
                    <li className="breadcrumb-item"><a href="#" onClick={() => navigate('/superAdmin/Agents')}>Agents</a></li>
                    <li className="breadcrumb-item active">Primes d'enrôlement</li>
                  </ol>
                  <h4 className="main-title mb-0 d-flex align-items-center">
                    <PersonPlus className="me-2 text-primary" size={28} />
                    Primes d'enrôlement
                  </h4>
                  <p className="text-muted mb-0">{formatCurrency(0.50)} par contribuable enrôlé</p>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <Button variant="primary" size="sm" onClick={handleCalculateAll} 
                    disabled={calculatingAll || totalAgents === 0 || !isDatesValid} 
                    className="d-flex align-items-center shadow-sm">
                    {calculatingAll ? <Spinner animation="border" size="sm" className="me-2" /> : <ArrowRepeat className="me-2" size={16} />}
                    Calculer tous
                  </Button>
                  <Button variant="warning" size="sm" onClick={handleRecalculateCurrentPage} 
                    disabled={autoRegenerating || agents.length === 0 || !isDatesValid} 
                    className="d-flex align-items-center shadow-sm">
                    {autoRegenerating ? <Spinner animation="border" size="sm" className="me-2" /> : <ArrowRepeat className="me-2" size={16} />}
                    Recalculer page
                  </Button>
                  <Button variant="success" size="sm" className="d-flex align-items-center" onClick={handleExportExcel} 
                    disabled={exporting || totalAgents === 0 || !isDatesValid}>
                    {exporting ? <Spinner animation="border" size="sm" className="me-2" /> : <FileEarmarkExcel className="me-2" size={16} />}
                    Excel
                  </Button>
                  <Button variant="danger" size="sm" className="d-flex align-items-center" onClick={handleExportPDF} 
                    disabled={exporting || totalAgents === 0 || !isDatesValid}>
                    {exporting ? <Spinner animation="border" size="sm" className="me-2" /> : <FilePdf className="me-2" size={16} />}
                    PDF
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {error && (
            <Alert variant="danger" className="mb-4 border-0 shadow-sm">
              <XCircle className="me-2" /> {error}
            </Alert>
          )}

          {/* Indicateur de recalcul en cours */}
          {isRecalculating && (
            <Alert variant="info" className="mb-4 border-0 shadow-sm d-flex align-items-center">
              <Spinner animation="border" size="sm" className="me-2" />
              <span>Recalcul des primes en cours pour la page {currentPage}...</span>
            </Alert>
          )}

          {/* Statistiques */}
          {stats && stats.total_amount > 0 && (
            <Row className="g-3 mb-4">
              <StatCard title="Agents" value={stats.total_agents} icon={<Person className="text-primary" size={22} />} color="primary" />
              <StatCard title="Contribuables" value={stats.total_contribuables} icon={<PeopleFill className="text-success" size={22} />} color="success" />
              <StatCard title="Total primes" value={formatCurrency(accountingData?.debit || stats.total_amount || 0)} icon={<CurrencyDollar className="text-warning" size={22} />} color="warning" subtitle={`${formatCurrency(stats.rate_per_contribuable)} / contribuable`} progress={stats.total_amount > 0 ? Math.round((stats.paid_amount / stats.total_amount) * 100) : 0} />
              <StatCard title="Payé" value={formatCurrency(accountingData?.credit || stats.paid_amount || 0)} icon={<CreditCard className="text-info" size={22} />} color="info" subtitle={`En attente: ${formatCurrency(accountingData?.balance || stats.pending_amount || 0)}`} />
            </Row>
          )}

          {/* Synthèse comptable */}
          {accountingData && (
            <Row className="mb-4">
              <Col md={12}>
                <Card className={`border-0 shadow-sm rounded-3 ${accountingData.isSettled ? 'border-success border-2' : 'border-warning border-2'}`}>
                  <Card.Body>
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                      <div>
                        <h6 className="fw-bold mb-2 d-flex align-items-center">
                          <Wallet2 className="me-2" size={20} />
                          Synthèse comptable - Page {currentPage}
                        </h6>
                        <div className="d-flex flex-wrap gap-4">
                          <div><span className="text-muted small text-uppercase d-block">DÉBIT</span><span className="h5 fw-bold text-danger">{formatCurrency(accountingData.debit)}</span></div>
                          <div><span className="text-muted small text-uppercase d-block">CRÉDIT</span><span className="h5 fw-bold text-success">{formatCurrency(accountingData.credit)}</span></div>
                          <div><span className="text-muted small text-uppercase d-block">SOLDE</span><span className={`h5 fw-bold ${accountingData.balance > 0 ? 'text-warning' : 'text-success'}`}>{formatCurrency(Math.abs(accountingData.balance))}</span></div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <Badge bg={accountingData.isSettled ? 'success' : 'warning'} pill className="px-4 py-2 fs-6">
                          {accountingData.isSettled ? <><CheckCircle className="me-1" size={16} /> SOLDÉ</> : <><Clock className="me-1" size={16} /> NON SOLDÉ</>}
                        </Badge>
                        <Button variant="outline-primary" size="sm" onClick={() => setShowAccountingModal(true)} className="d-flex align-items-center">
                          <InfoCircle className="me-1" size={16} /> Détails
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Filtres - Version avec validation manuelle */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm rounded-3">
                <Card.Body>
                  <Row className="g-3">
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-bold small">
                          <Calendar className="me-1" /> Date de début
                          <span className="text-danger ms-1">*</span>
                        </Form.Label>
                        <Form.Control 
                          type="date" 
                          value={tempStartDate} 
                          onChange={(e) => setTempStartDate(e.target.value)} 
                          className={`bg-light border-0 ${!tempStartDate ? 'border-danger' : ''}`}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-bold small">
                          <Calendar className="me-1" /> Date de fin
                          <span className="text-danger ms-1">*</span>
                        </Form.Label>
                        <Form.Control 
                          type="date" 
                          value={tempEndDate} 
                          onChange={(e) => setTempEndDate(e.target.value)} 
                          className={`bg-light border-0 ${!tempEndDate ? 'border-danger' : ''}`}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label className="fw-bold small"><Search className="me-1" /> Rechercher</Form.Label>
                        <InputGroup>
                          <InputGroup.Text className="bg-light border-0"><Search /></InputGroup.Text>
                          <Form.Control 
                            placeholder="Agent, email..." 
                            value={tempSearchTerm} 
                            onChange={(e) => setTempSearchTerm(e.target.value)} 
                            className="bg-light border-0" 
                          />
                          {tempSearchTerm && (
                            <Button variant="outline-secondary" className="border-0" onClick={() => setTempSearchTerm('')}>
                              <XCircle size={16} />
                            </Button>
                          )}
                        </InputGroup>
                      </Form.Group>
                    </Col>
                   
                    

                     <Col md={1}>
                      <Form.Group>
                        <Form.Label className="fw-bold small">Par page</Form.Label>
                        <Form.Select 
                          value={tempItemsPerPage} 
                          onChange={(e) => setTempItemsPerPage(Number(e.target.value))} 
                          className="bg-light border-0"
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                     <Col md={3}>
                      <Form.Label className="fw-bold small"></Form.Label>
                      <div className="d-flex gap-2 flex-wrap">
                        
                        <Button 
                          variant="primary" 
                          onClick={handleApplyFilters}
                          className="d-flex align-items-center"
                          disabled={isLoading || isRecalculating}
                        >
                          {isLoading ? (
                            <Spinner animation="border" size="sm" className="me-2" />
                          ) : (
                            <Search className="me-2" size={16} />
                          )}
                          Valider les filtres
                        </Button>
                        <Button 
                          variant="outline-secondary" 
                          onClick={handleResetFilters}
                          className="d-flex align-items-center"
                          disabled={isLoading}
                        >
                          <ArrowRepeat className="me-2" size={16} />
                          
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  
                  {/* Boutons d'action des filtres */}
                
                  
                  {/* Indicateur des filtres actifs */}
                  <Row className="mt-2">
                    <Col md={12}>
                      <small className="text-muted">
                        <strong>Période :</strong> {formatPeriod()}
                        {searchTerm && <Badge bg="info" className="ms-2">Filtre: {searchTerm}</Badge>}
                        {isRecalculating && <Badge bg="info" className="ms-2"><Spinner animation="border" size="sm" className="me-1" /> Recalcul...</Badge>}
                        {autoRegenerating && <Badge bg="warning" className="ms-2"><Spinner animation="border" size="sm" className="me-1" /> En cours...</Badge>}
                      </small>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Liste des agents */}
          <Row>
            <Col md={12}>
              <Card className="shadow-sm border-0 rounded-3">
                <Card.Header className="bg-primary d-flex justify-content-between align-items-center py-3 border-bottom">
                  <div className="d-flex align-items-center">
                    <Briefcase className="me-2 text-white" />
                    <span className="fw-semibold text-white">Liste des agents ({pagination.total})</span>
                    {searchTerm && <Badge bg="info" className="ms-2 rounded-pill">Filtre actif</Badge>}
                    {accountingData && <Badge bg={accountingData.isSettled ? 'success' : 'warning'} className="ms-2 rounded-pill px-3 py-1">{accountingData.isSettled ? 'Soldé' : 'Non soldé'}</Badge>}
                    {(startDate !== firstDayStr || endDate !== todayStr) && (
                      <Badge bg="secondary" className="ms-2 rounded-pill">Période personnalisée</Badge>
                    )}
                  </div>
                  <div className="text-white small d-flex align-items-center">
                    <span>Page {pagination.currentPage}/{pagination.lastPage}</span>
                    <span className="mx-2">|</span>
                    <span>Affichage {pagination.from}-{pagination.to} / {pagination.total}</span>
                  </div>
                </Card.Header>
                
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0 align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th>Agent</th>
                          <th className="text-center">Contribuables</th>
                          <th className="text-center">Débit (Dû)</th>
                          <th className="text-center">Crédit (Payé)</th>
                          <th className="text-center">Solde</th>
                          <th className="text-center">Statut</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agents.length === 0 ? (
                          <tr><td colSpan="7" className="text-center py-5"><InfoCircle size={48} className="text-muted opacity-25" /><h5 className="mt-3 text-muted">AUCUN AGENT TROUVÉ</h5></td></tr>
                        ) : (
                          agents.map((agent) => {
                            const bonus = bonuses.find(b => b.user_id === agent.user_id);
                            const hasBonus = bonus && parseFloat(bonus.total_amount || 0) > 0;
                            const debit = bonus ? parseFloat(bonus.total_amount || 0) : 0;
                            const credit = bonus ? parseFloat(bonus.total_paid || 0) : 0;
                            const balance = debit - credit;
                            const isSettled = balance <= 0;
                            
                            return (
                              <tr key={agent.user_id}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-2"><Person size={14} className="text-primary" /></div>
                                    <div>
                                      <strong>{agent.nom_complet}</strong>
                                      {agent.is_active === false && <Badge bg="secondary" className="ms-2">Inactif</Badge>}
                                      {bonus?.has_complement && <Badge bg="info" className="ms-1"><PlusCircle size={10} className="me-1" />{bonus.complements?.length || 0}</Badge>}
                                    </div>
                                  </div>
                                </td>
                                <td className="text-center"><Badge bg="primary" pill className="px-3 py-2">{bonus?.contribuables_count || 0}</Badge></td>
                                <td className="text-center"><span className="text-danger fw-bold">{formatCurrency(debit)}</span></td>
                                <td className="text-center"><span className="text-success fw-bold">{formatCurrency(credit)}</span></td>
                                <td className="text-center"><span className={`fw-bold ${isSettled ? 'text-success' : 'text-warning'}`}>{formatCurrency(Math.abs(balance))}</span></td>
                                <td className="text-center">
                                  {bonus ? (
                                    <Badge bg={bonus.is_fully_paid ? 'success' : 'warning'} pill className="px-3 py-2">
                                      {bonus.is_fully_paid ? <><CheckCircle className="me-1" size={12} /> Payée</> : <><Clock className="me-1" size={12} /> En attente</>}
                                    </Badge>
                                  ) : <Badge bg="secondary" pill className="px-3 py-2"><Clock className="me-1" size={12} /> Non calculée</Badge>}
                                </td>
                                <td className="text-center">
                                  <div className="d-flex gap-1 justify-content-center">
                                    {hasBonus && !bonus.is_fully_paid && (
                                      <Button variant="outline-success" size="sm" onClick={() => {
                                        const balance2 = debit - credit;
                                        let pendingComplement = bonus.complements?.find(c => c.status === 'pending' && parseFloat(c.amount) > 0);
                                        if (pendingComplement) {
                                          setSelectedBonus({...pendingComplement, agent: bonus.agent, total_amount: debit, total_paid: credit, remaining_balance: balance2, id: pendingComplement.id});
                                        } else if (bonus.status === 'pending') {
                                          setSelectedBonus({...bonus, amount: balance2 > 0 ? balance2 : bonus.amount, agent: bonus.agent, total_amount: debit, total_paid: credit, remaining_balance: balance2, is_complement: false, id: bonus.id});
                                        } else {
                                          showToastNotification('⚠️ Attention', 'Aucune prime en attente', 'warning');
                                          return;
                                        }
                                        setShowPayModal(true);
                                      }}><Wallet2 size={14} /></Button>
                                    )}
                                    <Button variant="outline-primary" size="sm" onClick={() => handleCalculateSingleAgent(agent.user_id, agent.nom_complet)} disabled={calculating} className="rounded-circle" style={{ width: '34px', height: '34px', padding: 0 }}>
                                      {calculating ? <Spinner animation="border" size="sm" /> : <ArrowRepeat size={14} />}
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {pagination.total > 0 && (
                    <div className="p-3 border-top d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">
                      <div className="text-muted small">
                        Affichage de <strong>{pagination.from}</strong> à <strong>{pagination.to}</strong> sur <strong>{pagination.total}</strong> agents
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <span className="text-muted small">
                          Total débit: <strong className="text-danger">{formatCurrency(agents.reduce((sum, agent) => {
                            const bonus = bonuses.find(b => b.user_id === agent.user_id);
                            return sum + (bonus ? parseFloat(bonus.total_amount || 0) : 0);
                          }, 0))}</strong>
                        </span>
                        <span className="text-muted small">
                          Total crédit: <strong className="text-success">{formatCurrency(agents.reduce((sum, agent) => {
                            const bonus = bonuses.find(b => b.user_id === agent.user_id);
                            return sum + (bonus ? parseFloat(bonus.total_paid || 0) : 0);
                          }, 0))}</strong>
                        </span>
                        <Pagination className="mb-0">{getPaginationItems()}</Pagination>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* MODALES - Les mêmes que précédemment */}

        {/* Modal de calcul pour un agent */}
        <Modal show={showCalculateModal} onHide={() => setShowCalculateModal(false)} centered>
          <Modal.Header closeButton className="border-0" style={{ background: "linear-gradient(135deg, #198754, #157347)" }}>
            <Modal.Title className="text-white d-flex align-items-center">
              <PersonPlus className="me-2" />
              Calculer pour un agent
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <p className="small text-muted">Sélectionnez un agent pour la période du <strong>{formatPeriod()}</strong>.</p>
            
            {agents.length === 0 ? (
              <Alert variant="warning" className="small">
                <InfoCircle className="me-2" />
                Aucun agent disponible
              </Alert>
            ) : (
              <Select
                options={agentOptions}
                value={agentOptions.find(opt => opt.value === selectedAgent) || null}
                onChange={(option) => setSelectedAgent(option ? option.value : null)}
                placeholder="Choisissez un agent..."
                isClearable
                isSearchable
                className="border-0"
                styles={{
                  control: (base) => ({
                    ...base,
                    border: 'none',
                    backgroundColor: '#f8f9fa',
                    padding: '4px 0',
                    boxShadow: 'none',
                    minHeight: '44px',
                    '&:hover': { border: 'none' }
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isFocused ? '#e9ecef' : 'transparent',
                    color: '#212529',
                    cursor: 'pointer',
                    padding: '10px 12px',
                    '&:active': { backgroundColor: '#0d6efd', color: 'white' }
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 1050,
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: '#6c757d'
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: '#212529',
                    fontWeight: '500'
                  })
                }}
                noOptionsMessage={() => "Aucun agent trouvé"}
              />
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="secondary" onClick={() => setShowCalculateModal(false)}>
              Annuler
            </Button>
            <Button 
              variant="success" 
              onClick={handleCalculateForAgent}
              disabled={calculating || !selectedAgent || agents.length === 0 || !isDatesValid}
            >
              {calculating ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <CheckCircle className="me-2" />
              )}
              Calculer
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de résultat */}
        <Modal 
          show={showResultModal} 
          onHide={() => setShowResultModal(false)} 
          centered
          size="lg"
          className="result-modal"
        >
          <Modal.Header closeButton className="border-0" style={{ background: "linear-gradient(135deg, #198754, #157347)" }}>
            <Modal.Title className="text-white d-flex align-items-center">
              <Award className="me-2" size={24} />
              Résultat du calcul
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4" style={{maxHeight:'500px',overflowY:'auto'}}>
            {resultData && (
              <>
                <div className="text-center mb-4">
                  <div className="bg-success bg-opacity-10 rounded-circle p-3 d-inline-block mb-3">
                    <CheckCircle size={48} className="text-success" />
                  </div>
                  <h4 className="fw-bold mb-1">Prime calculée avec succès</h4>
                  <p className="text-muted small">
                    Pour la période du <strong>{formatPeriod()}</strong>
                  </p>
                </div>

                <Card className="border-0 bg-light mb-4">
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <div className="d-flex align-items-center mb-2">
                          <Person className="text-primary me-2" size={18} />
                          <span className="fw-bold">{resultData?.agent_name || 'Agent'}</span>
                        </div>
                        <div className="d-flex align-items-center">
                          <Building className="text-muted me-2" size={16} />
                          <small className="text-muted">ID: {resultData.agent_id}</small>
                        </div>
                      </Col>
                      <Col md={6} className="text-md-end">
                        {(() => {
                          const totalPending = parseFloat(resultData.total_pending || 0);
                          const totalAmount = parseFloat(resultData.total_amount || 0);
                          
                          if (totalPending > 0.01) {
                            return (
                              <Badge bg="warning" className="p-2" style={{ fontSize: '0.9rem' }}>
                                <Clock className="me-1" size={14} />
                                En attente ({formatCurrency(totalPending)} restant)
                              </Badge>
                            );
                          } else if (totalAmount > 0) {
                            return (
                              <Badge bg="success" className="p-2" style={{ fontSize: '0.9rem' }}>
                                <CheckCircle className="me-1" size={14} />
                                Payée
                              </Badge>
                            );
                          } else {
                            return (
                              <Badge bg="warning" className="p-2" style={{ fontSize: '0.9rem' }}>
                                <Clock className="me-1" size={14} />
                                En attente
                              </Badge>
                            );
                          }
                        })()}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Card className="border-0 shadow-sm mb-4">
                  <Card.Body>
                    <Row className="text-center">
                      <Col md={4} className="border-end">
                        <div className="text-muted small text-uppercase">DÉBIT (Dû)</div>
                        <div className="h4 fw-bold text-danger mb-0">
                          {formatCurrency(resultData.total_amount || 0)}
                        </div>
                        <small className="text-muted">Total de la prime</small>
                      </Col>
                      <Col md={4} className="border-end">
                        <div className="text-muted small text-uppercase">CRÉDIT (Payé)</div>
                        <div className="h4 fw-bold text-success mb-0">
                          {formatCurrency(resultData.total_paid || 0)}
                        </div>
                        <small className="text-muted">Déjà payé</small>
                      </Col>
                      <Col md={4}>
                        <div className="text-muted small text-uppercase">SOLDE</div>
                        <div className={`h4 fw-bold mb-0 ${parseFloat(resultData.total_pending || 0) > 0.01 ? 'text-warning' : 'text-success'}`}>
                          {formatCurrency(Math.abs(parseFloat(resultData.total_pending || 0)))}
                        </div>
                        <small className="text-muted">
                          {parseFloat(resultData.total_pending || 0) > 0.01 ? 'Restant dû' : 'Soldé'}
                        </small>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {resultData.has_complement && (
                  <Alert variant="info" className="mb-3">
                    <PlusCircle className="me-2" size={16} />
                    <strong>Compléments :</strong> {resultData.complements?.length || 0} complément(s)
                    {resultData.pending_complement && (
                      <span className="ms-2">
                        <Badge bg="warning">
                          {formatCurrency(resultData.pending_complement.amount)} en attente
                        </Badge>
                      </span>
                    )}
                  </Alert>
                )}

                <Row className="mb-4 g-3">
                  <Col md={4}>
                    <Card className="border-0 shadow-sm text-center h-100">
                      <Card.Body>
                        <div className="text-muted small text-uppercase fw-bold mb-2">Contribuables</div>
                        <h4 className="display-7 fw-bold text-primary mb-0">
                          {resultData.enrolment_count || 0}
                        </h4>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="border-0 shadow-sm text-center h-100">
                      <Card.Body>
                        <div className="text-muted small text-uppercase fw-bold mb-2">Taux / contribuable</div>
                        <h4 className="display-7 fw-bold text-success mb-0">
                          {formatCurrency(resultData.amount_per_contribuable || 0.50)}
                        </h4>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="border-0 shadow-sm text-center h-100">
                      <Card.Body>
                        <div className="text-muted small text-uppercase fw-bold mb-2">Statut global</div>
                        <h4 className="display-7 fw-bold mb-0">
                          {parseFloat(resultData.total_pending || 0) > 0.01 ? (
                            <Badge bg="warning" className="fs-6">
                              <Clock className="me-1" size={14} />
                              En attente
                            </Badge>
                          ) : parseFloat(resultData.total_amount || 0) > 0 ? (
                            <Badge bg="success" className="fs-6">
                              <CheckCircle className="me-1" size={14} />
                              Payée
                            </Badge>
                          ) : (
                            <Badge bg="warning" className="fs-6">
                              <Clock className="me-1" size={14} />
                              En attente
                            </Badge>
                          )}
                        </h4>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {resultData.contribuables && resultData.contribuables.length > 0 && (
                  <>
                    <h6 className="fw-bold mb-3">
                      <PeopleFill className="me-2" />
                      Contribuables enrôlés ({resultData.contribuables.length})
                    </h6>
                    <div className="table-responsive" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <Table hover size="sm" className="mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th>#</th>
                            <th>Nom</th>
                            <th>Date d'enrôlement</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resultData.contribuables.map((c, idx) => (
                            <tr key={c.id || idx}>
                              <td>{idx + 1}</td>
                              <td>{c.nom || c.nom_contribuable || `Contribuable ${idx + 1}`}</td>
                              <td>{formatDate(c.date_enrollement || c.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </>
                )}

                {(!resultData.contribuables || resultData.contribuables.length === 0) && (
                  <Alert variant="info" className="small mb-0">
                    <InfoCircle className="me-2" />
                    Aucun contribuable enrôlé pour cette période.
                  </Alert>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="outline-secondary" onClick={() => setShowResultModal(false)}>
              Fermer
            </Button>
            {resultData && parseFloat(resultData.total_pending || 0) > 0.01 && (
              <Button 
                variant="success"
                onClick={() => {
                  let bonusToPay = null;
                  
                  if (resultData.pending_complement) {
                    bonusToPay = {
                      id: resultData.pending_complement.id,
                      amount: resultData.pending_complement.amount,
                      status: resultData.pending_complement.status,
                      contribuables_count: resultData.pending_complement.contribuables_count || 0,
                      bonus_type: 'enrollement_complement',
                      agent: {
                        name: resultData.agent_name,
                        id: resultData.agent_id
                      },
                      user_id: resultData.agent_id,
                      total_amount: resultData.total_amount,
                      total_paid: resultData.total_paid,
                      remaining_balance: resultData.total_pending
                    };
                  } else {
                    bonusToPay = {
                      id: resultData.bonus_id,
                      amount: resultData.total_pending,
                      status: 'pending',
                      contribuables_count: resultData.enrolment_count || 0,
                      bonus_type: 'enrollement',
                      agent: {
                        name: resultData.agent_name,
                        id: resultData.agent_id
                      },
                      user_id: resultData.agent_id,
                      total_amount: resultData.total_amount,
                      total_paid: resultData.total_paid,
                      remaining_balance: resultData.total_pending
                    };
                  }
                  
                  if (bonusToPay) {
                    setSelectedBonus(bonusToPay);
                    setShowResultModal(false);
                    setShowPayModal(true);
                  } else {
                    showToastNotification('⚠️ Attention', 'Aucune prime en attente à payer', 'warning');
                  }
                }}
              >
                <Wallet2 className="me-2" />
                Payer {formatCurrency(resultData.total_pending)} restant
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        {/* Modal Détails Comptables */}
        <Modal 
          show={showAccountingModal} 
          onHide={() => setShowAccountingModal(false)} 
          centered
          size="lg"
        >
          <Modal.Header closeButton className="border-0" style={{ background: "linear-gradient(135deg, #0d6efd, #0b5ed7)" }}>
            <Modal.Title className="text-white d-flex align-items-center">
              <Wallet2 className="me-2" size={24} />
              Détails comptables - Page {currentPage}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {accountingData && (
              <>
                <div className="text-center mb-4">
                  <h5 className="fw-bold">Période du {formatPeriod()}</h5>
                  <p className="text-muted small">Suivi des primes d'enrôlement - Page {currentPage}/{pagination.lastPage}</p>
                </div>

                <Row className="g-3 mb-4">
                  <Col md={4}>
                    <Card className="border-0 bg-light text-center h-100">
                      <Card.Body>
                        <div className="text-muted small text-uppercase fw-bold">Total primes</div>
                        <h3 className="fw-bold text-primary">{accountingData.totalBonuses}</h3>
                        <small className="text-muted">primes calculées</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="border-0 bg-success bg-opacity-10 text-center h-100">
                      <Card.Body>
                        <div className="text-muted small text-uppercase fw-bold">Payées</div>
                        <h3 className="fw-bold text-success">{accountingData.paidCount}</h3>
                        <small className="text-muted">primes payées</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="border-0 bg-warning bg-opacity-10 text-center h-100">
                      <Card.Body>
                        <div className="text-muted small text-uppercase fw-bold">En attente</div>
                        <h3 className="fw-bold text-warning">{accountingData.pendingCount}</h3>
                        <small className="text-muted">primes non payées</small>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Card className="border-0 shadow-sm mb-4">
                  <Card.Body>
                    <Row className="text-center">
                      <Col md={4} className="border-end">
                        <div className="text-muted small text-uppercase">DÉBIT</div>
                        <div className="h4 fw-bold text-danger mb-0">{formatCurrency(accountingData.debit)}</div>
                        <small className="text-muted">Total des primes dues</small>
                      </Col>
                      <Col md={4} className="border-end">
                        <div className="text-muted small text-uppercase">CRÉDIT</div>
                        <div className="h4 fw-bold text-success mb-0">{formatCurrency(accountingData.credit)}</div>
                        <small className="text-muted">Total des primes payées</small>
                      </Col>
                      <Col md={4}>
                        <div className="text-muted small text-uppercase">SOLDE</div>
                        <div className={`h4 fw-bold mb-0 ${accountingData.balance > 0 ? 'text-warning' : 'text-success'}`}>
                          {formatCurrency(Math.abs(accountingData.balance))}
                        </div>
                        <small className="text-muted">
                          {accountingData.balance > 0 ? 'Restant dû' : 'Crédit excédentaire'}
                        </small>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {accountingData.balance > 0 ? (
                  <Alert variant="warning" className="d-flex align-items-center">
                    <Clock className="me-2" size={20} />
                    <div>
                      <strong>Compte non soldé</strong> - Il reste <strong>{formatCurrency(accountingData.balance)}</strong> à payer pour solder cette page.
                    </div>
                  </Alert>
                ) : (
                  <Alert variant="success" className="d-flex align-items-center">
                    <CheckCircle className="me-2" size={20} />
                    <div>
                      <strong>Compte soldé</strong> - Toutes les primes de cette page ont été payées.
                    </div>
                  </Alert>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="secondary" onClick={() => setShowAccountingModal(false)}>
              Fermer
            </Button>
            {accountingData && accountingData.balance > 0 && (
              <Button variant="primary" onClick={() => setShowAccountingModal(false)}>
                <Wallet2 className="me-2" />
                Gérer les paiements
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        {/* Modal Paiement */}
        <Modal show={showPayModal} onHide={() => setShowPayModal(false)} centered>
          <Modal.Header closeButton className="border-0" style={{ background: "linear-gradient(135deg, #0d6efd, #0b5ed7)" }}>
            <Modal.Title className="text-white d-flex align-items-center">
              <Wallet2 className="me-2" />
              Payer la prime
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {selectedBonus && (
              <>
                <div className="bg-light p-3 rounded mb-3">
                  <Row className="small">
                    <Col md={6}>
                      <span className="text-muted">Agent</span>
                      <p className="fw-bold mb-0">{selectedBonus.agent?.name || `Agent ${selectedBonus.user_id}`}</p>
                    </Col>
                    <Col md={6}>
                      <span className="text-muted">Montant à payer</span>
                      <p className="fw-bold text-success mb-0" style={{ fontSize: '1.2rem' }}>
                        {formatCurrency(selectedBonus.amount || selectedBonus.remaining_balance || 0)}
                      </p>
                    </Col>
                  </Row>
                  <Row className="mt-2">
                    <Col md={6}>
                      <span className="text-muted">Contribuables</span>
                      <p className="fw-bold mb-0">{selectedBonus.contribuables_count || 0}</p>
                    </Col>
                    <Col md={6}>
                      <span className="text-muted">Période</span>
                      <p className="fw-bold mb-0">{formatPeriod()}</p>
                    </Col>
                  </Row>
                </div>
                
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold small">Mode de paiement</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      id="payment-bank"
                      label="Banque"
                      value="bank_transfer"
                      checked={paymentMethod === 'bank_transfer'}
                      onChange={(e) => {
                        setPaymentMethod(e.target.value);
                        setPaymentReference('');
                        setMobileNumber('');
                      }}
                      className="fw-bold"
                    />
                    <Form.Check
                      type="radio"
                      id="payment-mobile"
                      label="Mobile Money"
                      value="mobile_money"
                      checked={paymentMethod === 'mobile_money'}
                      onChange={(e) => {
                        setPaymentMethod(e.target.value);
                        setPaymentReference('');
                        setMobileNumber('');
                      }}
                      className="fw-bold"
                    />
                  </div>
                </Form.Group>

                {paymentMethod === 'bank_transfer' ? (
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small">
                      <FileText className="me-1" size={14} />
                      Référence du virement
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Entrez la référence du virement bancaire..."
                      value={paymentReference}
                      onChange={(e) => setPaymentReference(e.target.value)}
                      className="border-0 bg-light py-2"
                      required
                    />
                    <Form.Text className="text-muted small">
                      Ex: VIREMENT-2026-07-21-001
                    </Form.Text>
                  </Form.Group>
                ) : paymentMethod === 'mobile_money' ? (
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small">
                      <Phone className="me-1" size={14} />
                      Numéro Mobile Money
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="Entrez le numéro Mobile Money (ex: 06 12 34 56 78)"
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(e.target.value)}
                      className="border-0 bg-light py-2"
                      required
                    />
                    <Form.Text className="text-muted small">
                      Numéro de téléphone associé au compte Mobile Money
                    </Form.Text>
                  </Form.Group>
                ) : null}

                <div className="mt-3 p-3 bg-success bg-opacity-10 rounded text-center border border-success border-opacity-25">
                  <small className="text-muted text-uppercase">Vous allez payer</small>
                  <p className="h4 fw-bold text-success mb-0">
                    {formatCurrency(selectedBonus.amount || selectedBonus.remaining_balance || 0)}
                  </p>
                  <small className="text-muted">
                    {paymentMethod === 'bank_transfer' ? 'Virement bancaire' : 'Mobile Money'}
                  </small>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="secondary" onClick={() => {
              setShowPayModal(false);
              setPaymentReference('');
              setMobileNumber('');
              setMobileOperator('orange');
              setPaymentNotes('');
            }}>
              Annuler
            </Button>
            <Button 
              variant="success"
              onClick={handlePayBonus}
              disabled={paying || !selectedBonus || parseFloat(selectedBonus.amount || selectedBonus.remaining_balance || 0) <= 0 || !isDatesValid}
            >
              {paying ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <CheckCircle className="me-2" />
              )}
              Confirmer le paiement
            </Button>
          </Modal.Footer>
        </Modal>

        <style>{`
          .stats-card { transition: transform 0.3s ease, box-shadow 0.3s ease; border-radius: 12px; overflow: hidden; position: relative; }
          .stats-card:hover { transform: translateY(-5px); box-shadow: 0 10px 30px rgba(0,0,0,0.1) !important; }
          .card-gradient { position: absolute; top: 0; left: 0; width: 100%; height: 4px; opacity: 0.8; }
          .result-modal .modal-content { border-radius: 16px; overflow: hidden; }
          .form-control:focus, .form-select:focus { border-color: #0d6efd; box-shadow: 0 0 0 0.2rem rgba(13,110,253,0.25); }
          .table > :not(caption) > * > * { padding: 0.75rem 0.75rem; }
          .btn-sm { padding: 0.25rem 0.5rem; }
        `}</style>

        <Footer />
      </div>
    </>
  );
};

export default EnrolmentBonuses;